import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import ApiService from '@/services/api.js'

export function useHeroSlides(locale) {
  // Reactive state
  const heroSlides = ref([])
  const currentSlide = ref(0)
  const slideInterval = ref(null)
  const slideDuration = 5000 // 5 saniye

  // Hero slides yükleme
  const loadHeroSlides = async () => {
    try {
      console.log('🔄 Loading hero slides...')
      const slidesData = await ApiService.getHeroSlides()
      console.log('🎭 Hero slides API response:', slidesData)
      
      if (slidesData && slidesData.length > 0) {
        heroSlides.value = slidesData.map(slide => ({
          id: slide.id,
          title: locale.value === 'tr' ? (slide.title_tr || slide.title) : (slide.title_en || slide.title) || 'Aslan Energy',
          description: locale.value === 'tr' ? (slide.description_tr || slide.description) : (slide.description_en || slide.description) || 'Sürdürülebilir enerji çözümleri',
          backgroundImage: slide.backgroundImage || slide.image_url || 'https://images.unsplash.com/photo-1497440001374-f26997328c1b',
          primaryButtonText: locale.value === 'tr' ? (slide.button_text_tr || slide.primaryButtonText || 'Keşfet') : (slide.button_text_en || slide.primaryButtonText || 'Explore'),
          secondaryButtonText: locale.value === 'tr' ? 'İletişim' : 'Contact',
          primaryAction: slide.primaryAction || slide.button_action || 'explore',
          secondaryAction: slide.secondaryAction || 'contact'
        }))
        console.log('✅ Hero slides loaded:', heroSlides.value.length, 'slides')
        // Slides yüklendikten sonra otomatik slider'ı başlat
        startSlider()
      } else {
        console.warn('⚠️ No hero slides data received, using fallback')
        // Fallback slides
        setFallbackSlides()
        // Fallback slides yüklendikten sonra da slider'ı başlat
        startSlider()
      }
    } catch (error) {
      console.error('❌ Hero slides yüklenemedi:', error)
      setFallbackSlides()
      // Hata durumunda fallback slides ile slider'ı başlat
      startSlider()
    }
  }

  // Fallback slides
  const setFallbackSlides = () => {
    heroSlides.value = [
      {
        id: 1,
        title: locale.value === 'tr' ? 'Aslan Energy' : 'Aslan Energy',
        description: locale.value === 'tr' ? 'Sürdürülebilir enerji çözümleri ile geleceği inşa ediyoruz' : 'Building the future with sustainable energy solutions',
        backgroundImage: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
        primaryButtonText: locale.value === 'tr' ? 'Keşfet' : 'Explore',
        secondaryButtonText: locale.value === 'tr' ? 'İletişim' : 'Contact',
        primaryAction: 'explore',
        secondaryAction: 'contact'
      },
      {
        id: 2,
        title: locale.value === 'tr' ? 'Güneş Enerjisi' : 'Solar Energy',
        description: locale.value === 'tr' ? 'Temiz ve yenilenebilir enerji sistemleri' : 'Clean and renewable energy systems',
        backgroundImage: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
        primaryButtonText: locale.value === 'tr' ? 'Projeler' : 'Projects',
        secondaryButtonText: locale.value === 'tr' ? 'Teklif Al' : 'Get Quote',
        primaryAction: 'projects',
        secondaryAction: 'quote'
      }
    ]
  }

  // Slider navigation
  const goToSlide = (index) => {
    currentSlide.value = index
    resetSlideTimer()
  }

  const nextSlide = () => {
    currentSlide.value = (currentSlide.value + 1) % heroSlides.value.length
    resetSlideTimer()
  }

  const prevSlide = () => {
    currentSlide.value = currentSlide.value === 0 
      ? heroSlides.value.length - 1 
      : currentSlide.value - 1
    resetSlideTimer()
  }

  // Slide actions
  const handleSlideAction = (action) => {
    console.log('🎬 Slide action:', action)
    switch (action) {
      case 'explore':
        document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' })
        break
      case 'contact':
        document.querySelector('.modern-footer')?.scrollIntoView({ behavior: 'smooth' })
        break
      case 'projects':
        document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' })
        break
      case 'services':
        document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' })
        break
      case 'machinery':
        document.querySelector('.machinery-section')?.scrollIntoView({ behavior: 'smooth' })
        break
      case 'quote':
        document.querySelector('.contact-form-section')?.scrollIntoView({ behavior: 'smooth' })
        break
      default:
        console.log('Unknown action:', action)
    }
  }

  // Auto slide functionality
  const startSlider = () => {
    if (heroSlides.value.length > 1) {
      slideInterval.value = setInterval(() => {
        nextSlide()
      }, slideDuration)
    }
  }

  const stopSlider = () => {
    if (slideInterval.value) {
      clearInterval(slideInterval.value)
      slideInterval.value = null
    }
  }

  const resetSlideTimer = () => {
    stopSlider()
    startSlider()
  }

  // Initialize
  onMounted(() => {
    loadHeroSlides()
  })

  onUnmounted(() => {
    stopSlider()
  })

  // Watch locale changes
  watch(locale, () => {
    loadHeroSlides()
  })

  return {
    heroSlides,
    currentSlide,
    goToSlide,
    nextSlide,
    prevSlide,
    handleSlideAction,
    startSlider,
    stopSlider,
    loadHeroSlides
  }
}